import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import OrderDetailModal from './OrderDetailModal';
import PrintModal from './PrintModal';
import AddUnitsModal from './AddUnitsModal';
import UploadModal from '../../ProjectManager/UploadManager/UploadModal';
import './Inventory.css';

const Inventory = () => {
    const [kitTypes, setKitTypes] = useState([]);
    const [selectedKitType, setSelectedKitType] = useState(null);
    const [kitUnits, setKitUnits] = useState([]);
    const [filteredKitUnits, setFilteredKitUnits] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [showAddUnitsModal, setShowAddUnitsModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadData, setUploadData] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [unitsFilter, setUnitsFilter] = useState('');
    const [statusFilters, setStatusFilters] = useState([]);
    const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
    const filterDropdownRef = useRef(null);

    const BASE_URL = 'https://service9004.szapfs.org';

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
                setFilterDropdownOpen(false);
            }
        };
        if (filterDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [filterDropdownOpen]);

    // Fetch kit types on component mount
    useEffect(() => {
        fetchKitTypes();
        fetchOrders();
    }, []);

    const fetchKitTypes = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${BASE_URL}/api/kittypes`);
            if (!response.ok) throw new Error('Failed to fetch kit types');
            const data = await response.json();

            // Fetch activated counts for each kit type
            const typesWithCounts = await Promise.all(data.map(async (kitType) => {
                try {
                    const unitsResponse = await fetch(`${BASE_URL}/api/kittypes/${kitType.KitID}/kitunits`);
                    if (unitsResponse.ok) {
                        const units = await unitsResponse.json();
                        const activatedCount = units.filter(u => u.ActivationDate && !u.Clinician_Validated).length;
                        return { ...kitType, activatedCount };
                    }
                    return { ...kitType, activatedCount: 0 };
                } catch {
                    return { ...kitType, activatedCount: 0 };
                }
            }));

            setKitTypes(typesWithCounts);
            setError(null);
        } catch (err) {
            setError('Failed to load kit types');
            console.error('Error fetching kit types:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchOrders = async () => {
        try {
            const response = await fetch(`${BASE_URL}/api/orders`);
            if (!response.ok) throw new Error('Failed to fetch orders');
            const data = await response.json();
            setOrders(data);
        } catch (err) {
            console.error('Error fetching orders:', err);
        }
    };

    const fetchKitUnits = async (kitTypeId) => {
        try {
            setActionLoading(true);
            const response = await fetch(`${BASE_URL}/api/kittypes/${kitTypeId}/kitunits`);
            if (!response.ok) throw new Error('Failed to fetch kit units');
            const data = await response.json();
            setKitUnits(data);
            setFilteredKitUnits(data);
        } catch (err) {
            console.error('Error fetching kit units:', err);
            setKitUnits([]);
            setFilteredKitUnits([]);
        } finally {
            setActionLoading(false);
        }
    };

    // Filter and sort kit units based on search and status
    useEffect(() => {
        let filtered = [...kitUnits];

        // Apply text filter
        if (unitsFilter.trim()) {
            const filterLower = unitsFilter.toLowerCase();
            filtered = filtered.filter(unit =>
                (unit.KitUnitID && unit.KitUnitID.toLowerCase().includes(filterLower)) ||
                (unit.ActivationID && unit.ActivationID.toLowerCase().includes(filterLower)) ||
                (unit.Status && unit.Status.toLowerCase().includes(filterLower)) ||
                (unit.UserID && unit.UserID.toString().toLowerCase().includes(filterLower)) ||
                (unit.OrderID && unit.OrderID.toString().toLowerCase().includes(filterLower)) ||
                (unit.CreationDate && unit.CreationDate.toLowerCase().includes(filterLower))
            );
        }

        // Helper to get derived status key
        const getDerivedKey = (unit) => {
            if (unit.Clinician_Validated) return 'clinicianvalidating';
            if (unit.Lab_Received) return 'labreceived';
            if (unit.ActivationDate) return 'activated';
            return 'inactive';
        };

        // Apply status filters (multi-select)
        if (statusFilters.length > 0) {
            filtered = filtered.filter(unit => {
                const derivedKey = getDerivedKey(unit);
                // Check if any selected filter matches
                return statusFilters.some(filter => {
                    if (filter === 'printed') return unit.Printed != null;
                    if (filter === 'notprinted') return unit.Printed == null;
                    return derivedKey === filter;
                });
            });
        }

        // Sort by derived status priority
        const statusPriority = {
            'clinicianvalidating': 1,
            'labreceived': 2,
            'activated': 3,
            'inactive': 4
        };

        filtered.sort((a, b) => {
            const statusA = statusPriority[getDerivedKey(a)] || 999;
            const statusB = statusPriority[getDerivedKey(b)] || 999;
            return statusA - statusB;
        });

        setFilteredKitUnits(filtered);
    }, [kitUnits, unitsFilter, statusFilters]);

    const handleKitTypeClick = (kitType) => {
        setSelectedKitType(kitType);
        fetchKitUnits(kitType.KitID);
    };

    const handleUnitClick = (unit) => {
        const order = orders.find(o => o.OrderID === unit.OrderID);
        setSelectedUnit({
            ...unit,
            orderDetails: order,
            kitTypeInfo: selectedKitType ? {
                name: selectedKitType.Name,
                imageURL: selectedKitType.ImageURL
            } : null
        });
        setShowOrderModal(true);
    };

    const handleAddUnits = async (quantity) => {
        if (!selectedKitType) return;
        try {
            setActionLoading(true);
            const response = await fetch(`${BASE_URL}/api/kittypes/${selectedKitType.KitID}/kitunits`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ quantity }),
            });
            if (!response.ok) throw new Error('Failed to add kit units');

            // Close modal and refresh kit units after adding
            setShowAddUnitsModal(false);
            fetchKitUnits(selectedKitType.KitID);
        } catch (err) {
            console.error('Error adding kit units:', err);
        } finally {
            setActionLoading(false);
        }
    };

    const deleteKitUnit = async (unitId) => {
        try {
            setActionLoading(true);
            const response = await fetch(`${BASE_URL}/api/kitunits/${unitId}`, {
                method: 'DELETE',
            });
            if (!response.ok) throw new Error('Failed to delete kit unit');
            
            // Refresh kit units after deleting
            if (selectedKitType) {
                fetchKitUnits(selectedKitType.KitID);
            }
        } catch (err) {
            console.error('Error deleting kit unit:', err);
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        return new Date(dateString).toLocaleDateString();
    };

    // Derive the current status from timeline stages (latest stage wins)
    const getDerivedStatus = (unit) => {
        if (unit.Clinician_Validated) return { label: 'Clinician Validating', icon: 'fas fa-user-md', color: '#497caf' };
        if (unit.Lab_Received) return { label: 'Lab Received', icon: 'fas fa-vial', color: '#497caf' };
        if (unit.ActivationDate) return { label: 'Activated', icon: 'fas fa-check-circle', color: '#10b981' };
        return { label: 'Inactive', icon: 'fas fa-clock', color: '#9ca3af' };
    };

    // Status filter options matching derived statuses
    const statusOptions = [
        { value: 'inactive', label: 'Inactive', icon: 'fas fa-clock' },
        { value: 'activated', label: 'Activated', icon: 'fas fa-check-circle' },
        { value: 'labreceived', label: 'Lab Received', icon: 'fas fa-vial' },
        { value: 'clinicianvalidating', label: 'Clinician Validating', icon: 'fas fa-user-md' },
        { value: 'printed', label: 'Printed', icon: null },
        { value: 'notprinted', label: 'Not Printed', icon: null },
    ];

    const toggleFilter = (value) => {
        setStatusFilters(prev =>
            prev.includes(value)
                ? prev.filter(f => f !== value)
                : [...prev, value]
        );
    };

// src/components/Inventory.js

const handlePrintLabels = async (quantity) => {
    if (!selectedKitType) {
        setError("No kit type selected.");
        return;
    }

    setActionLoading(true);
    setError(null);

    try {
        // Step 1: Call the existing endpoint to create exactly 'quantity' new units.
        const response = await fetch(`${BASE_URL}/api/kittypes/${selectedKitType.KitID}/kitunits`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ quantity }),
        });

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || 'Failed to generate new kit units');
        }

        // Step 2: Use the response directly to prepare for printing.
        // The backend conveniently returns the IDs it just created.
        const { generated_ids } = data;
        if (!generated_ids || generated_ids.length === 0) {
            throw new Error('API did not return the new kit IDs.');
        }

        const unitsToPrint = generated_ids.map(id => ({ ActivationID: id }));

        // Step 3: Open the local print view with the new units.
        openPrintView(unitsToPrint);
        
        // Step 4: Close the modal and refresh the unit list in the background.
        setShowPrintModal(false);
        fetchKitUnits(selectedKitType.KitID);

    } catch (err) {
        console.error('Error printing labels:', err);
        setError(err.message);
    } finally {
        // The setActionLoading(false) will be handled by the fetchKitUnits call,
        // but we can set it here too as a fallback.
        setActionLoading(false);
    }
};

    const openPrintView = (units) => {
        // Clean up any previous iframe to be safe
        const oldIframe = document.getElementById('print-iframe-labels');
        if (oldIframe) {
            oldIframe.remove();
        }

        // Create an invisible iframe
        const iframe = document.createElement('iframe');
        iframe.id = 'print-iframe-labels';
        iframe.style.display = 'none';
        document.body.appendChild(iframe);

        // Write the content to be printed into the iframe
        iframe.contentDocument.write(`
            <html>
                <head>
                    <title>Print Kit Labels</title>
                    <style>
                        body { font-family: sans-serif; }
                        .label-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
                        .label { border: 1px solid #ccc; padding: 10px; text-align: center; page-break-inside: avoid; }
                        .activation-id { font-weight: bold; font-size: 1.2em; }
                        @media print {
                            body { margin: 0.5in; }
                            .label-grid { gap: 15px; }
                        }
                    </style>
                </head>
                <body>
                    <div class="label-grid">
                        ${units.map(unit => `
                            <div class="label">
                                <div>Kit Unit ID:</div>
                                <div class="activation-id">${unit.ActivationID || unit.KitUnitID.slice(-8)}</div>
                            </div>
                        `).join('')}
                    </div>
                </body>
            </html>
        `);
        iframe.contentDocument.close();

        // Set up cleanup to run after printing is done
        iframe.contentWindow.onafterprint = () => {
            iframe.remove();
        };

        // Focus and print
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
    };

    if (loading) {
        return (
            <div className="GORDU-inventory-container">
                <div className="GORDU-loading">Loading inventory...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="GORDU-inventory-container">
                <div className="GORDU-error">{error}</div>
            </div>
        );
    }

    return (
        <div className="GORDU-inventory-container">
            {/* Left Panel - Kit Types */}
            <div className="GORDU-kit-types-panel">
                <div className="GORDU-panel-header">
                    <div className="GORDU-header-content">
                        <i className="fas fa-boxes GORDU-header-icon"></i>
                        <span className="GORDU-header-title">Kit Types</span>
                    </div>
                    <div className="GORDU-kit-count">{kitTypes.length}</div>
                </div>
                <div className="GORDU-kit-types-list">
                    {kitTypes.map((kitType) => (
                        <div
                            key={kitType.KitID}
                            className={`GORDU-kit-type-chip ${selectedKitType?.KitID === kitType.KitID ? 'GORDU-selected' : ''}`}
                            onClick={() => handleKitTypeClick(kitType)}
                        >
                            <div className="GORDU-kit-image-small">
                                <img 
                                    src={kitType.ImageURL} 
                                    alt={kitType.Name}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                                <div className="GORDU-kit-fallback-icon" style={{display: 'none'}}>
                                    <i className="fas fa-flask"></i>
                                </div>
                            </div>
                            <div className="GORDU-kit-info-compact">
                                <div className="GORDU-kit-name-small">{kitType.Name}</div>
                            </div>
                            {kitType.activatedCount > 0 && (
                                <div className="GORDU-activated-badge">
                                    <span>{kitType.activatedCount}</span>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Right Panel - Kit Units */}
            <div className="GORDU-kit-units-panel">
                {selectedKitType ? (
                    <>
                        <div className="GORDU-panel-header">
                            <div className="GORDU-header-content">
                                <i className="fas fa-cubes GORDU-header-icon"></i>
                                <div>
                                    <span className="GORDU-header-title">{selectedKitType.Name}</span>
                                    <div className="GORDU-panel-subtitle">{filteredKitUnits.length} of {kitUnits.length} units</div>
                                </div>
                            </div>
                            <div className="GORDU-header-actions">
                                <button 
                                    className="GORDU-btn GORDU-btn-secondary"
                                    onClick={() => setShowPrintModal(true)}
                                    disabled={actionLoading}
                                >
                                    <i className="fas fa-print"></i>
                                    Print Labels
                                </button>
                                <button
                                    className="GORDU-btn GORDU-btn-secondary"
                                    onClick={() => setShowAddUnitsModal(true)}
                                    disabled={actionLoading}
                                >
                                    <i className="fas fa-plus"></i>
                                    Add Units
                                </button>
                            </div>
                        </div>
                        
                        {/* Filters */}
                        <div className="GORDU-filters-container">
                            <div className="GORDU-search-container">
                                <i className="fas fa-search GORDU-search-icon"></i>
                                <input
                                    type="text"
                                    className="GORDU-search-input"
                                    placeholder="Search units..."
                                    value={unitsFilter}
                                    onChange={(e) => setUnitsFilter(e.target.value)}
                                />
                            </div>
                            <div className="GORDU-filter-dropdown-container" ref={filterDropdownRef}>
                                <button
                                    className={`GORDU-filter-dropdown-btn ${statusFilters.length > 0 ? 'has-filters' : ''}`}
                                    onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
                                >
                                    <i className="fas fa-filter"></i>
                                    <span>Filter{statusFilters.length > 0 ? ` (${statusFilters.length})` : ''}</span>
                                    <i className={`fas fa-chevron-${filterDropdownOpen ? 'up' : 'down'}`}></i>
                                </button>
                                {filterDropdownOpen && (
                                    <div className="GORDU-filter-dropdown-menu">
                                        {statusOptions.map(opt => (
                                            <label
                                                key={opt.value}
                                                className={`GORDU-filter-option ${statusFilters.includes(opt.value) ? 'selected' : ''}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={statusFilters.includes(opt.value)}
                                                    onChange={() => toggleFilter(opt.value)}
                                                />
                                                {opt.icon && <i className={opt.icon}></i>}
                                                <span>{opt.label}</span>
                                            </label>
                                        ))}
                                        {statusFilters.length > 0 && (
                                            <button
                                                className="GORDU-filter-clear-btn"
                                                onClick={() => setStatusFilters([])}
                                            >
                                                <i className="fas fa-times"></i>
                                                Clear All
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {actionLoading ? (
                            <div className="GORDU-loading">
                                <i className="fas fa-spinner fa-spin"></i>
                                Loading units...
                            </div>
                        ) : (
                            <div className="GORDU-kit-units-grid">
                                {filteredKitUnits.length === 0 ? (
                                    <div className="GORDU-empty-state">
                                        <i className="fas fa-inbox GORDU-empty-icon"></i>
                                        <div className="GORDU-empty-title">
                                            {kitUnits.length === 0 ? 'No Units Available' : 'No matching units'}
                                        </div>
                                        <div className="GORDU-empty-subtitle">
                                            {kitUnits.length === 0 ? 'Add some units to get started' : 'Try adjusting your filters'}
                                        </div>
                                    </div>
                                ) : (
                                    filteredKitUnits.map((unit) => (
                                        <div
                                            key={unit.KitUnitID}
                                            className="GORDU-unit-chip"
                                            onClick={() => handleUnitClick(unit)}
                                        >
                                            {(() => {
                                                const status = getDerivedStatus(unit);
                                                return (
                                                    <div className="GORDU-unit-status-circle" style={{ borderColor: status.color }} title={status.label}>
                                                        <i className={status.icon} style={{ color: status.color }}></i>
                                                    </div>
                                                );
                                            })()}
                                            <div className="GORDU-unit-chip-content">
                                                <div className="GORDU-unit-activation-id">
                                                    {unit.ActivationID || unit.KitUnitID.slice(-8)}
                                                </div>
                                                <div className="GORDU-unit-creation-date">
                                                    {formatDate(unit.CreationDate)}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <div className="GORDU-empty-state">
                        <i className="fas fa-arrow-left GORDU-empty-icon"></i>
                        <div className="GORDU-empty-title">Select a Kit Type</div>
                        <div className="GORDU-empty-subtitle">Choose a kit type from the left panel to view its units</div>
                    </div>
                )}
            </div>

            {/* Order Detail Modal via Portal */}
            {showOrderModal && selectedUnit && createPortal(
                <OrderDetailModal
                    unit={selectedUnit}
                    isOpen={showOrderModal}
                    onClose={() => {
                        setShowOrderModal(false);
                        setSelectedUnit(null);
                    }}
                    onRefresh={() => {
                        if (selectedKitType) {
                            fetchKitUnits(selectedKitType.KitID);
                        }
                    }}
                    onOpenUploadModal={(data) => {
                        setUploadData(data);
                        setShowOrderModal(false);
                        setSelectedUnit(null);
                        setShowUploadModal(true);
                    }}
                />,
                document.body
            )}

            {/* Upload Modal via Portal */}
            {showUploadModal && createPortal(
                <UploadModal
                    isOpen={showUploadModal}
                    onClose={() => {
                        console.log('Closing upload modal');
                        setShowUploadModal(false);
                        setUploadData(null);
                    }}
                    externalPatientId={uploadData?.patientId}
                    kitInfo={uploadData ? {
                        activationId: uploadData.activationId,
                        patientName: uploadData.patientName,
                        patientEmail: uploadData.patientEmail,
                        prefilledData: uploadData.prefilledData,
                        kitTypeInfo: uploadData.kitTypeInfo
                    } : null}
                    onUploadStatusChange={(status) => console.log('Upload status:', status)}
                />,
                document.body
            )}


{showPrintModal && selectedKitType && createPortal(
    <PrintModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        onPrint={handlePrintLabels}
        kitType={selectedKitType}
        actionLoading={actionLoading}
    />,
    document.body
)}

{showAddUnitsModal && selectedKitType && createPortal(
    <AddUnitsModal
        isOpen={showAddUnitsModal}
        onClose={() => setShowAddUnitsModal(false)}
        onAdd={handleAddUnits}
        kitType={selectedKitType}
        actionLoading={actionLoading}
    />,
    document.body
)}
        </div>
    );
};

export default Inventory;
